<template>

    <validation-observer ref="ratesValidator" v-slot="{ passes, errors }" slim>
        <ping :api-base-url="apiBaseUrl" :interval-in-minutes="5"></ping>
        <div>

            <sticky-panel cssClass="sticky-panel mb-1" :show-panel-switches="false">

                <b-container fluid>
                    <b-row class="bg-light border border-gray-800 pt-3">

                        <b-col cols="5">

                            <b-form-group label-size="sm" label-cols-xl="4" :label="relatedTo">
                                
                                <b-form-select v-model="selectedOption" 
                                               size="sm" 
                                               v-on:change="selectionChanged" 
                                               class="align-bottom"
                                               placeholder="Organisation or Court/Tribunal/other">

                                    <b-form-select-option-group label="Court/Tribunal/other">
                                        <b-form-select-option v-for="(option,index) in allCourtRegistryOptions" :value="option" :key="index">
                                            {{ option.text }}
                                        </b-form-select-option>
                                    </b-form-select-option-group>

                                    <b-form-select-option-group label="Interview Organisation">
                                        <b-form-select-option v-for="(option,index) in allInterviewOrganisationOptions" :value="option" :key="index">
                                            {{ option.text }}
                                        </b-form-select-option>
                                    </b-form-select-option-group>

                                </b-form-select>
                            </b-form-group>
                        </b-col>
                        <b-col cols="3">
                            <b-form-group label-size="sm" label-cols-xl="3" label="Period">
                                <b-form-select :options="rateGroups" size="sm" text-field="displayText" value-field="quoteRateGroupId" v-model="selectedQuoteRateGroupId" @change="rateGroupSelectionChanged"></b-form-select>
                            </b-form-group>
                        </b-col>
                        <b-col cols="4">
                            <b-row>
                                <b-col>
                                    <b-form-group label-size="sm" label-cols-xl="2" label="From" v-if="selectedRateGroup">
                                        <b-form-datepicker size="sm"
                                                           :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                                                           placeholder="undefined"
                                                           locale="en-AU" reset-button
                                                           v-model="validFromDate"
                                                           :disabled="(selectedRateGroup.publishedDateTimeUTC != null)"
                                                           @input="ratesDirty = true"></b-form-datepicker>
                                    </b-form-group>
                                </b-col>
                                <b-col>
                                    <b-form-group label-size="sm" label-cols-xl="2" label="To" v-if="selectedRateGroup">
                                        <b-form-datepicker size="sm"
                                                           :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                                                           placeholder="undefined"
                                                           locale="en-AU" right reset-button
                                                           v-model="validToDate"
                                                           :min="validFromDate"
                                                           :disabled="(selectedRateGroup.publishedDateTimeUTC != null || !validFromDate)"
                                                           @input="ratesDirty = true"></b-form-datepicker>
                                    </b-form-group>
                                </b-col>
                            </b-row>
                        </b-col>
                        <b-col cols="3">
                            <b-form-group label-size="sm" label-cols-xl="6" label="Client Deposit" v-if="selectedRateGroup">
                                <b-form-select size="sm" :options="percentageOptions" v-model="clientDepositPercentage" :disabled="(selectedRateGroup.publishedDateTimeUTC != null)" @change="ratesDirty = true" />
                            </b-form-group>
                        </b-col>

                        <b-col cols="3">
                            <b-form-group label-size="sm" label-cols-xl="6" label="External Deposit" v-if="selectedRateGroup">
                                <b-form-select size="sm" :options="percentageOptions" v-model="externalDepositPercentage" :disabled="(selectedRateGroup.publishedDateTimeUTC != null)" @change="ratesDirty = true" />
                            </b-form-group>
                        </b-col>

                        <b-col cols="2">
                            <b-form-group label-size="sm" label-cols-xl="6" label="Folios/Hour" v-if="selectedRateGroup">
                                <b-form-input size="sm" type="number" step="0.5" v-model.number="foliosPerAudioHour" :disabled="(selectedRateGroup.publishedDateTimeUTC != null)" @change="ratesDirty = true" />
                            </b-form-group>
                        </b-col>
                        <b-col cols="2">
                            <b-form-group label-size="sm" label-cols-xl="6" label="Pages/Hour" v-if="selectedRateGroup">
                                <b-form-input size="sm" type="number" step="0.5" v-model="pagesPerAudioHour" :disabled="(selectedRateGroup.publishedDateTimeUTC != null)" @change="ratesDirty = true" />
                            </b-form-group>
                        </b-col>


                        <b-col cols="2" class="d-flex flex-column justify-content-end mb-3">
                            <b-button-toolbar class="justify-content-end">
                                <b-button size="sm" :variant="ratesDirty ? 'success' : 'secondary'" v-if="selectedRateGroup && (selectedRateGroup.publishedDateTimeUTC == null)" class="mx-1" @click="passes(saveRates)">Save</b-button>
                                <b-button size="sm" :variant="ratesDirty ? 'danger' : 'secondary'" v-if="selectedRateGroup && (selectedRateGroup.publishedDateTimeUTC == null)" class="mx-1" @click="resetRates">Reset</b-button>
                                <b-button size="sm" :variant="ratesDirty ? 'secondary' : 'warning'" v-if="selectedRateGroup && !isEmptyUuid(selectedRateGroup.quoteRateGroupId) && (selectedRateGroup.validFromDate != null) && (selectedRateGroup.publishedDateTimeUTC == null)" class="mx-1" @click="passes(publishRates)" :disabled="ratesDirty">Publish</b-button>
                            </b-button-toolbar>

                            <div v-if="selectedRateGroup && selectedRateGroup.publishedDateTimeUTC != null" class="text-right font-weight-bold">
                                Published:
                                {{ formatDateTime(selectedRateGroup.publishedDateTimeUTC, 'DD/MM/YYYY h:mmA', true, false) }}

                            </div>

                        </b-col>

                        <b-col cols="3">
                            <b-form-group label-size="sm" label-cols-xl="6" label="Fixed Bank Fee" v-if="selectedRateGroup">
                                <currency-input v-model="fixedBankCharge"
                                                :currency="$config.defaultCurrencyCode"
                                                class="text-right form-control form-control-sm"
                                                :disabled="(selectedRateGroup.publishedDateTimeUTC != null)"
                                                @change="ratesDirty = true"></currency-input>
                            </b-form-group>
                        </b-col>

                        <b-col cols="3">
                            <b-form-group label-size="sm" label-cols-xl="6" label="Variable Bank Fee (%)" v-if="selectedRateGroup">
                                <currency-input v-model="variableBankChargePercentage"
                                                :currency="{suffix: '%'}"
                                                class="text-right form-control form-control-sm"
                                                :disabled="(selectedRateGroup.publishedDateTimeUTC != null)"
                                                @change="ratesDirty = true"></currency-input>
                            </b-form-group>
                        </b-col>

                        <b-col cols="2">
                            <b-form-group label-size="sm" label-cols-xl="6" label="Minimum Charge" v-if="selectedRateGroup">
                                <currency-input v-model="minimumCharge"
                                                :currency="$config.defaultCurrencyCode"
                                                class="text-right form-control form-control-sm"
                                                :disabled="(selectedRateGroup.publishedDateTimeUTC != null)"
                                                @change="ratesDirty = true"></currency-input>
                            </b-form-group>
                        </b-col>

                        <b-col cols="2">
                            <b-form-group label-size="sm" label-cols-xl="6" label="Cost Center" v-if="selectedRateGroup">

                                <datalist id="costCenterList">
                                    <option v-for="(costCenter,index) in historicalCostCenters" :key="index">{{ costCenter }}</option>
                                </datalist>
                                <datalist id="materialCodeList">
                                    <option v-for="(materialCode,index) in historicalMaterialCodes" :key="index">{{ materialCode }}</option>
                                </datalist>
                                <b-input v-model="costCenter"
                                         list="costCenterList"
                                         class="form-control form-control-sm"
                                         :disabled="(selectedRateGroup.publishedDateTimeUTC != null)"
                                         
                                         @change="ratesDirty = true"></b-input>
                            </b-form-group>
                        </b-col>
                    </b-row>

                </b-container>

                </sticky-panel>

                <br style="clear:both">

                <b-alert :show="displayGeneralErrorMessage(errors)" variant="danger" dismissible style="width: 1200px; margin-left:30px;">
                    Please correct all errors below. Problems are marked with a red border. A rate > 0 requires a valid rate unit.
                </b-alert>

                <b-tabs content-class="mt-3" v-if="selectedRateGroup" v-model="activeTab" small>

                    <b-tab title="Service Rates">

                        <div style="margin:30px;" v-for="(serviceType,index1) in serviceTypes" :key="index1">

                            <h5 style="font-size: 22px;"> {{ serviceType.serviceTypeName }}</h5>
                            <b-table-simple hover bordered small caption-top responsive text-center :fixed="false" style="width:1200px; color: red;">

                                <b-thead>
                                    <b-tr style="font-size: 18px;">
                                        <b-th></b-th>
                                        <b-th>
                                            <b-input size="sm" placeholder="mat.code" 
                                                     class="border border-info bg-light" 
                                                     v-model="serviceType.clientMaterialCode"
                                                     v-on:blur="materialCodeChanged(serviceType.clientMaterialCode)"
                                                     list="materialCodeList"></b-input>
                                        </b-th>
                                        <b-th class="text-center" colspan="5">Client Rates</b-th>
                                        <b-th>
                                            <b-input size="sm" placeholder="mat.code" 
                                                     class="border border-info bg-light"
                                                     v-model="serviceType.externalMaterialCode"
                                                     v-on:blur="materialCodeChanged(serviceType.externalMaterialCode)"
                                                     list="materialCodeList"></b-input>
                                        </b-th>
                                        <b-th class="text-center" colspan="5">External Rates</b-th>
                                    </b-tr>

                                    <b-tr style="font-size: 14px;">

                                        <b-th></b-th>
                                        <b-th></b-th>

                                        <b-th class="text-center" colspan="2">Time Rate</b-th>
                                        <b-th class="text-center" colspan="2">Production Rate</b-th>

                                        <b-th></b-th>
                                        <b-th></b-th>

                                        <b-th class="text-center" colspan="2">Time Rate</b-th>
                                        <b-th class="text-center" colspan="2">Production Rate</b-th>

                                        <b-th></b-th>

                                    </b-tr>
                                </b-thead>

                                <b-tbody>
                                    <b-tr v-for="(serviceDelay,index2) in serviceType.serviceDelays" :key="index2" class="text-center" style="font-size: 12px;">

                                        <b-td style="width:150px;" class="text-left">{{ serviceDelay.serviceDelayName }}</b-td>

                                        <b-td style="background-color: #ddd; width: 120px;">
                                            <b-input v-model="serviceDelay.clientMaterialCode"
                                                     class="text-left form-control form-control-sm border border-info bg-light"
                                                     placeholder="mat.code"
                                                     list="materialCodeList"
                                                     v-on:blur="materialCodeChanged(serviceDelay.clientMaterialCode)"
                                                     v-if="!isUuidNullOrEmpty(serviceDelay.serviceDelayId)"></b-input>
                                        </b-td>


                                        <b-td style="background-color: #ddd; width:100px;">
                                            <currency-input v-model="serviceDelay.quoteRates[0].clientRate"
                                                            :currency="$config.defaultCurrencyCode"
                                                            :precision="$config.precision.rates"
                                                            class="text-right form-control form-control-sm "
                                                            :disabled="(selectedRateGroup.publishedDateTimeUTC != null)"
                                                            @change="clientRateChanged(serviceDelay.quoteRates[0])"></currency-input>
                                        </b-td>
                                        <b-td style="background-color: #ddd; width:120px;">
                                            <validation-provider :rules="rateTypeValidationRules(serviceDelay.quoteRates[0].clientRate)" :name="'Client Attendance Rate ' + index1 + ' ' + index2" v-slot="{ valid, errors }" slim>
                                                <b-form-select size="sm"
                                                               :options="allAttendanceRateTypeOptions"
                                                               v-model="serviceDelay.quoteRates[0].clientRateType"
                                                               :disabled="(selectedRateGroup.publishedDateTimeUTC != null)"
                                                               :state="errors[0] ? false : null"
                                                               @change="ratesDirty = true" />
                                            </validation-provider>
                                        </b-td>

                                        <b-td style="background-color: #ddd; width:100px;">
                                            <currency-input v-model="serviceDelay.quoteRates[1].clientRate"
                                                            :currency="$config.defaultCurrencyCode"
                                                            :precision="$config.precision.rates"
                                                            class="text-right form-control form-control-sm"
                                                            :disabled="(selectedRateGroup.publishedDateTimeUTC != null)"
                                                            @change="clientRateChanged(serviceDelay.quoteRates[1])"></currency-input>
                                        </b-td>
                                        <b-td style="background-color: #ddd; width:120px;">
                                            <validation-provider :rules="rateTypeValidationRules(serviceDelay.quoteRates[1].clientRate)" :name="'Client Production Rate ' + index1 + ' ' + index2" v-slot="{ valid, errors }" slim>
                                                <b-form-select size="sm"
                                                               :options="allProductionRateTypeOptions"
                                                               v-model="serviceDelay.quoteRates[1].clientRateType"
                                                               :disabled="(selectedRateGroup.publishedDateTimeUTC != null)"
                                                               :state="errors[0] ? false : null"
                                                               @change="ratesDirty = true" />
                                            </validation-provider>
                                        </b-td>

                                        <b-td style="background-color: #ddd;">
                                            <b-form-checkbox size="sm" :checked="serviceDelay.quoteRates[0].clientRate != 0 || serviceDelay.quoteRates[1].clientRate != 0" @change="changeClientRow(serviceDelay)" class="align-middle" v-if="selectedRateGroup.publishedDateTimeUTC == null" />
                                        </b-td>

                                        <b-td style="width: 120px;">
                                            <b-input v-model="serviceDelay.externalMaterialCode"
                                                     class="text-left form-control form-control-sm border border-info  bg-light"
                                                     placeholder="mat.code"
                                                     list="materialCodeList"
                                                     v-on:blur="materialCodeChanged(serviceDelay.externalMaterialCode)"
                                                     v-if="!isUuidNullOrEmpty(serviceDelay.serviceDelayId)"></b-input>
                                        </b-td>

                                        <b-td style="width:100px;">
                                            <currency-input v-model="serviceDelay.quoteRates[0].externalRate"
                                                            :currency="$config.defaultCurrencyCode"
                                                            :precision="$config.precision.rates"
                                                            :disabled="(selectedRateGroup.publishedDateTimeUTC != null)"
                                                            class="text-right form-control form-control-sm"
                                                            @change="externalRateChanged(serviceDelay.quoteRates[0])"></currency-input>
                                        </b-td>
                                        <b-td style="width:120px;">
                                            <validation-provider :rules="rateTypeValidationRules(serviceDelay.quoteRates[0].externalRate)" :name="'External Attendance Rate ' + index1 + ' ' + index2" v-slot="{ valid, errors }" slim>
                                                <b-form-select size="sm"
                                                               :options="allAttendanceRateTypeOptions"
                                                               v-model="serviceDelay.quoteRates[0].externalRateType"
                                                               :disabled="(selectedRateGroup.publishedDateTimeUTC != null)"
                                                               :state="errors[0] ? false : null"
                                                               @change="ratesDirty = true" />
                                            </validation-provider>
                                        </b-td>

                                        <b-td style="width:100px;">
                                            <currency-input v-model="serviceDelay.quoteRates[1].externalRate"
                                                            :currency="$config.defaultCurrencyCode"
                                                            :precision="$config.precision.rates"
                                                            class="text-right form-control form-control-sm"
                                                            :disabled="(selectedRateGroup.publishedDateTimeUTC != null)"
                                                            @change="externalRateChanged(serviceDelay.quoteRates[1])"></currency-input>
                                        </b-td>
                                        <b-td style="width:120px;">
                                            <validation-provider :rules="rateTypeValidationRules(serviceDelay.quoteRates[1].externalRate)" :name="'External Production Rate ' + index1 + ' ' + index2" v-slot="{ valid, errors }" slim>
                                                <b-form-select size="sm"
                                                               :options="allProductionRateTypeOptions"
                                                               v-model="serviceDelay.quoteRates[1].externalRateType"
                                                               :disabled="(selectedRateGroup.publishedDateTimeUTC != null)"
                                                               :state="errors[0] ? false : null"
                                                               @change="ratesDirty = true" />
                                            </validation-provider>
                                        </b-td>

                                        <b-td>  <b-form-checkbox size="sm" :checked="serviceDelay.quoteRates[0].externalRate != 0 || serviceDelay.quoteRates[1].externalRate != 0" @change="changeExternalRow(serviceDelay)" class="align-middle" v-if="selectedRateGroup.publishedDateTimeUTC == null" /></b-td>

                                    </b-tr>
                                </b-tbody>

                            </b-table-simple>

                            <p style="font-size:14px;" v-if="(selectedRateGroup.publishedDateTimeUTC == null) && (copyFromServiceTypes(serviceType).length > 0)">
                                <b-dropdown size="sm" text="Copy rates from service type">
                                    <b-dropdown-item v-for="copyFromServiceType in copyFromServiceTypes(serviceType)" :key="copyFromServiceType.serviceTypeId" @click="copyRatesFromOtherTable(copyFromServiceType.serviceDelays, serviceType)">{{ copyFromServiceType.serviceTypeName }}</b-dropdown-item>
                                </b-dropdown>
                            </p>

                        </div>
                    </b-tab>

                    <b-tab title="Additional Charges">

                        <div classs="container" class="">

                            <b-list-group class="w-75" size="sm">

                                <b-row class="small text-center">
                                    <b-col cols="5" class="text-left small">Additional Charge</b-col>
                                    <b-col class="small">Material Code</b-col>
                                    <b-col class="small">Client Rate</b-col>
                                    <b-col class="small">Material Code</b-col>
                                    <b-col class="small">External Rate</b-col>
                                    <b-col class="small">Unit</b-col>
                                </b-row>

                                <b-list-group-item v-for="(nonProductionChargeType,index) in nonProductionChargeTypes" :key="index" class="p-2 m-0">

                                    <b-row>
                                        <b-col cols="5">
                                            <b-form-input size="sm" disabled :value="nonProductionChargeType.nonProductionChargeTypeName" class="bg-danger" />
                                        </b-col>
                                        <b-col>
                                            <b-input class="text-left form-control form-control-sm border border-info bg-light"
                                                     list="materialCodeList"
                                                     v-on:blur="materialCodeChanged(nonProductionChargeType.clientMaterialCode)"
                                                     v-model="nonProductionChargeType.clientMaterialCode">
                                            </b-input>
                                        </b-col>
                                        <b-col>
                                            <currency-input :currency="$config.defaultCurrencyCode"
                                                            :precision="$config.precision.rates"
                                                            class="text-right form-control form-control-sm"
                                                            v-model="nonProductionChargeType.clientRate"
                                                            placeholder="client rate"
                                                            @change="additionalChargeChanged(serviceDelay.quoteRates[0])">
                                            </currency-input>
                                        </b-col>
                                        <b-col>
                                            <b-input class="text-left form-control form-control-sm border border-info bg-light"
                                                     list="materialCodeList"
                                                     v-on:blur="materialCodeChanged(nonProductionChargeType.externalMaterialCode)"
                                                     v-model="nonProductionChargeType.externalMaterialCode">
                                            </b-input>
                                        </b-col>
                                        <b-col>
                                            <currency-input :currency="$config.defaultCurrencyCode"
                                                            :precision="$config.precision.rates"
                                                            class="text-right form-control form-control-sm"
                                                            placeholder="external rate"
                                                            v-model="nonProductionChargeType.externalRate"
                                                            @change="additionalChargeChanged(serviceDelay.quoteRates[0])">
                                            </currency-input>
                                        </b-col>
                                        <b-col>
                                            <b-form-input size="sm" disabled :value="nonProductionChargeType.units" />
                                        </b-col>

                                    </b-row>

                                </b-list-group-item>

                                <b-row class="small text-center">
                                    <b-col cols="5" class="text-left small">Additional Charge</b-col>
                                    <b-col class="small">Material Code</b-col>
                                    <b-col class="small">Client Rate</b-col>
                                    <b-col class="small">Material Code</b-col>
                                    <b-col class="small">External Rate</b-col>
                                    <b-col class="small">Unit</b-col>
                                </b-row>
                            </b-list-group><br /><br /><br />
                        </div>

                    </b-tab>

                </b-tabs>
        </div>
    </validation-observer>
</template>

<style scoped>

    table th {
        vertical-align: middle;
    }

    .text-props {
        font-size: 6px;
    }

    .form-control:disabled,
    .b-form-btn-label-control.form-control[aria-disabled="true"],
    .custom-select:disabled {
        color: #495057 !important;
        background-color: #ffffff !important;
        opacity: 1;
    }
</style>

<script>
    import Vue from 'vue';
    import { BootstrapVue } from 'bootstrap-vue'
    import axios from 'axios';
    import { CalendarPlugin, DatePickerPlugin } from '@syncfusion/ej2-vue-calendars';
    import VueCurrencyInput from 'vue-currency-input'

    import { ValidationProvider, ValidationObserver } from 'vee-validate';
    import { extend, localize } from 'vee-validate';
    import { required, email, excluded } from 'vee-validate/dist/rules';
    import en from 'vee-validate/dist/locale/en.json';
    import StickyPanel from './StickyPanel';
    extend('required', required);
    extend('email', email);
    extend('excluded', excluded);
    localize('en', en);

    import { NIL as emptyUuid } from 'uuid';
    import Ping from './Ping';
    import UuidMixins from '../mixins/UuidMixins';
    import ErrorsMixin from '../mixins/ErrorsMixin';
    import DateTimeMixin from '../mixins/DateTimeMixin';
    import Select2 from './Select2';

    Vue.use(BootstrapVue);
    Vue.use(CalendarPlugin);
    Vue.use(DatePickerPlugin);
    Vue.use(VueCurrencyInput);
    Vue.component('validation-provider', ValidationProvider);
    Vue.component('validation-observer', ValidationObserver);
    Vue.component('ping', Ping);
    Vue.component('select2', Select2);
    Vue.component('sticky-panel', StickyPanel);

    export default {
        name: 'Rates',
        mixins: [ErrorsMixin, DateTimeMixin, UuidMixins],
        props: {
            apiBaseUrl: {
                type: String,
                required: true
            },
            allCourtRegistryOptions: {
                type: Array,
                default: null
            },
            allInterviewOrganisationOptions: {
                type: Array,
                default: null
            },
            allAttendanceRateTypeOptions: {
                type: Array,
                default: null
            },
            allProductionRateTypeOptions: {
                type: Array,
                default: null
            },
        },
        data: function () {
            return {

                percentageOptions: [
                    { value: 0, text: 'no deposit' },
                    { value: 10, text: '10 %' },
                    { value: 20, text: '20 %' },
                    { value: 30, text: '30 %' },
                    { value: 40, text: '40 %' },
                    { value: 50, text: '50 %' },
                    { value: 60, text: '60 %' },
                    { value: 70, text: '70 %' },
                    { value: 80, text: '80 %' },
                    { value: 90, text: '90 %' },
                    { value: 100, text: '100 %' }
                ],
                relatedTo: 'Please select from the drop down:',
                selectedOption: null,
                selectedCourtRegistryId: null,
                selectedInterviewOrganisationId: null,
                // selectedOrderTypeId: null,
                rateGroups: null,
                selectedQuoteRateGroupId: null,
                selectedRateGroup: null,

                validFromDate: null,
                validToDate: null,
                clientDepositPercentage: null,
                externalDepositPercentage: null,
                foliosPerAudioHour: null,
                pagesPerAudioHour: null,
                fixedBankCharge: null,
                variableBankChargePercentage: null,
                minimumCharge: null,
                costCenter: null,
                activeTab: 0,

                serviceTypes: [],
                nonProductionChargeTypes: null,
                historicalCostCenters: null,
                historicalMaterialCodes: null,

                ratesDirty: false
            }
        },
        computed: {

           
        },

        methods: {

            materialCodeChanged: function (args) {
                if (args!= null && args != '' && !this.historicalMaterialCodes.includes(args)) {
                    this.historicalMaterialCodes.push(args);
                } 
            },

            displayGeneralErrorMessage: function (errors) {
                return (JSON.stringify(errors).search("is not a valid value"));
            },

            isEmptyUuid: function (uuid) {
                return (uuid == emptyUuid);
            },
            rateTypeValidationRules: function (rate) {
                if ((rate == null) || (rate <= 0)) return {};
                return { required: true, excluded: [0, "0"] };
            },

            copyFromServiceTypes: function (targetServiceType) {
                var targetServiceTypeHasServiceDelays = false;
                if (targetServiceType.serviceDelays != null) {
                    for (var i = 0; i < targetServiceType.serviceDelays.length; i++) {
                        if (targetServiceType.serviceDelays[i].serviceDelayId != emptyUuid) {
                            targetServiceTypeHasServiceDelays = true;
                            break;
                        }
                    }
                }
                var copyFromServiceTypes = [];
                if (this.serviceTypes != null) {
                    for (var j = 0; j < this.serviceTypes.length; j++) {
                        var serviceType = this.serviceTypes[j];
                        if (serviceType.serviceTypeId == targetServiceType.serviceTypeId) continue;
                        var serviceTypeHasServiceDelays = false;
                        if (serviceType.serviceDelays != null) {
                            for (var k = 0; k < serviceType.serviceDelays.length; k++) {
                                if (serviceType.serviceDelays[k].serviceDelayId != emptyUuid) {
                                    serviceTypeHasServiceDelays = true;
                                    break;
                                }
                            }
                        }
                        if (serviceTypeHasServiceDelays != targetServiceTypeHasServiceDelays) continue;
                        copyFromServiceTypes.push(serviceType);
                    }
                }
                return copyFromServiceTypes;
            },

            copyRatesFromOtherTable: function (serviceDelays, serviceType) {

                for (var i = 0; i < serviceDelays.length; i++) {

                    serviceType.serviceDelays[i].quoteRates[0].clientRate = serviceDelays[i].quoteRates[0].clientRate;
                    serviceType.serviceDelays[i].quoteRates[0].clientRateType = serviceDelays[i].quoteRates[0].clientRateType;

                    serviceType.serviceDelays[i].quoteRates[1].clientRate = serviceDelays[i].quoteRates[1].clientRate;
                    serviceType.serviceDelays[i].quoteRates[1].clientRateType = serviceDelays[i].quoteRates[1].clientRateType;

                    serviceType.serviceDelays[i].quoteRates[0].externalRate = serviceDelays[i].quoteRates[0].externalRate;
                    serviceType.serviceDelays[i].quoteRates[0].externalRateType = serviceDelays[i].quoteRates[0].externalRateType;

                    serviceType.serviceDelays[i].quoteRates[1].externalRate = serviceDelays[i].quoteRates[1].externalRate;
                    serviceType.serviceDelays[i].quoteRates[1].externalRateType = serviceDelays[i].quoteRates[1].externalRateType;
                }
            },

            clientRateChanged: function (quoteRate) {
                if ((quoteRate.clientRate == null) || (quoteRate.clientRate == 0)) {
                    quoteRate.clientRate = null;
                    quoteRate.clientRateType = 0;
                }
                this.ratesDirty = true;
            },
            externalRateChanged: function (quoteRate) {
                if ((quoteRate.externalRate == null) || (quoteRate.externalRate == 0)) {
                    quoteRate.externalRate = null;
                    quoteRate.externalRateType = 0;
                }
                this.ratesDirty = true;
            },

            additionalChargeChanged: function () {
               
            },

            // client row checkbox
            changeClientRow: function (rateObject) {
                rateObject.quoteRates[0].clientRate = null;
                rateObject.quoteRates[0].clientRateType = 0;
                rateObject.quoteRates[1].clientRate = null;
                rateObject.quoteRates[1].clientRateType = 0;
                this.ratesDirty = true;
            },

            // external row checkbox
            changeExternalRow: function (rateObject) {
                rateObject.quoteRates[0].externalRate = null;
                rateObject.quoteRates[0].externalRateType = 0;
                rateObject.quoteRates[1].externalRate = null;
                rateObject.quoteRates[1].externalRateType = 0;
                this.ratesDirty = true;
            },

            selectionChanged: function (obj) {
                if (obj.group == "CourtRegistry") {
                    this.relatedTo = "Court/Tribunal/Other";
                    this.selectedCourtRegistryId = obj.value;
                    this.selectedInterviewOrganisationId = null;
                } else if (obj.group == "InterviewOrganisation") {
                    this.relatedTo = "Interview Organisation";
                    this.selectedCourtRegistryId = null;
                    this.selectedInterviewOrganisationId = obj.value;
                } else {
                    this.relatedTo = null;
                    this.selectedCourtRegistryId = null;
                    this.selectedInterviewOrganisationId = null;
                }
                this.loadRateGroups();
            },

            loadRateGroups: function (selectQuoteRateGroupId) {
                this.rateGroups = null;
                this.selectedQuoteRateGroupId = null;
                this.selectedRateGroup = null;
                this.validFromDate = null;
                this.validToDate = null;
                this.clientDepositPercentage = null;
                this.externalDepositPercentage = null;
                this.foliosPerAudioHour = null;
                this.pagesPerAudioHour = null;
                this.serviceTypes = null;
                if (this.selectedCourtRegistryId || this.selectedInterviewOrganisationId) {

                    var url = this.apiBaseUrl;
                    if (!url.endsWith('/')) url += '/';
                    if (this.selectedCourtRegistryId) url += "quote/rategroups/bycourtregistry/" + this.selectedCourtRegistryId;
                    else url += "quote/rategroups/byintervieworganisation/" + this.selectedInterviewOrganisationId;

                    axios
                        .get(url)
                        .then(response => {
                            this.rateGroups = response.data;
                            console.log(JSON.stringify(this.rateGroups));

                            if (selectQuoteRateGroupId) this.selectedQuoteRateGroupId = selectQuoteRateGroupId;
                            else {
                                var unpublishedRateGroup = this.rateGroups.find(rg => (rg.publishedDateTimeUTC == null));
                                if (unpublishedRateGroup) this.selectedQuoteRateGroupId = unpublishedRateGroup.quoteRateGroupId;
                            }
                            this.rateGroupSelectionChanged();
                        })
                        .catch(ex => {
                            var currentErrors = this.handleAxiosException(ex); 
                            this.showErrorsToast(currentErrors);
                        });
                }
            },

            rateGroupSelectionChanged: function () {
                this.selectedRateGroup = null;
                this.validFromDate = null;
                this.validToDate = null;
                this.clientDepositPercentage = null;
                this.externalDepositPercentage = null;
                this.foliosPerAudioHour = null;
                this.pagesPerAudioHour = null;
                if (this.selectedQuoteRateGroupId) {
                    //this is required so that we can load the whole object that is currently selected - because that is required to display/bind the valid from/to dates
                    this.selectedRateGroup = this.rateGroups.find(rg => rg.quoteRateGroupId == this.selectedQuoteRateGroupId);
                    if (this.selectedRateGroup) {
                        this.validFromDate = this.selectedRateGroup.validFromDate;
                        this.validToDate = this.selectedRateGroup.validToDate;
                        this.clientDepositPercentage = this.selectedRateGroup.clientDepositPercentage;
                        this.externalDepositPercentage = this.selectedRateGroup.externalDepositPercentage;
                        this.foliosPerAudioHour = this.selectedRateGroup.foliosPerAudioHour;
                        this.pagesPerAudioHour = this.selectedRateGroup.pagesPerAudioHour;
                        this.fixedBankCharge = this.selectedRateGroup.fixedBankCharge;
                        this.variableBankChargePercentage = this.selectedRateGroup.variableBankChargePercentage;
                        this.minimumCharge = this.selectedRateGroup.minimumCharge;
                        this.costCenter = this.selectedRateGroup.costCenter;

                    }
                }
                this.loadRates();
                this.loadNonProductionCharges();
            },

            getHistoricalMaterialCodes: function () {

                var url = this.apiBaseUrl;
                if (!url.endsWith('/')) url += '/';
                url += 'Quote/historicalmaterialcodes';

                axios
                    .get(url)
                    .then(response => {
                        this.historicalMaterialCodes = response.data;
                    })
                    .catch(ex => {
                        var currentErrors = this.handleAxiosException(ex);
                        this.showErrorsToast(currentErrors);
                    });
            },

            getHistoricalCostCenters: function () {

                var url = this.apiBaseUrl;
                if (!url.endsWith('/')) url += '/';
                url += 'Quote/historicalcostcenters';

                axios
                    .get(url)
                    .then(response => {
                        this.historicalCostCenters = response.data;
                    })
                    .catch(ex => {
                        var currentErrors = this.handleAxiosException(ex);
                        this.showErrorsToast(currentErrors);
                    });
            },

            loadRates: function () {
                this.serviceTypes = null;
                this.ratesDirty = false;

                if ((this.selectedInterviewOrganisationId || this.selectedCourtRegistryId) && this.selectedQuoteRateGroupId) {

                    var url = this.apiBaseUrl;
                    if (!url.endsWith('/')) url += '/';

                    if (this.selectedCourtRegistryId) url += 'quote/rates/bycourtregistry/' + this.selectedCourtRegistryId + '/' + this.selectedQuoteRateGroupId;
                    else url += 'quote/rates/byintervieworganisation/' + this.selectedInterviewOrganisationId + '/' + this.selectedQuoteRateGroupId;
                    
                    axios
                        .get(url)
                        .then(response => {
                            this.serviceTypes = response.data;
                        })
                        .catch(ex => {
                            var currentErrors = this.handleAxiosException(ex);
                            this.showErrorsToast(currentErrors);
                        });
                }
            },
            loadNonProductionCharges: function () {

                if (this.selectedCourtRegistryId || this.selectedInterviewOrganisationId) {

                    var url = this.apiBaseUrl;
                    if (!url.endsWith('/')) url += '/';
                    if (this.selectedCourtRegistryId) url += 'quote/nonproductionchargetyperates/bycourtregistry/' + this.selectedCourtRegistryId + '/' + this.selectedQuoteRateGroupId;
                    else url += 'quote/nonproductionchargetyperates/byintervieworganisation/' + this.selectedInterviewOrganisationId + '/' + this.selectedQuoteRateGroupId;

                    axios
                        .get(url)
                        .then(response => {
                            this.nonProductionChargeTypes = response.data;
                        })
                        .catch(ex => {
                            var currentErrors = this.handleAxiosException(ex);
                            this.showErrorsToast(currentErrors);
                        });
                }
            },

            saveRates: function () {
                if (this.selectedQuoteRateGroupId) {
                    var data = {
                        quoteRateGroupId: this.selectedQuoteRateGroupId,
                        quoteOrderTypeId: null,
                        courtRegistryId: this.selectedCourtRegistryId,
                        interviewOrganisationId: this.selectedInterviewOrganisationId,
                        validFromDate: this.validFromDate,
                        validToDate: this.validToDate,
                        serviceTypes: this.serviceTypes,
                        nonProductionChargeTypes: this.nonProductionChargeTypes,
                        externalDepositPercentage: this.externalDepositPercentage,
                        clientDepositPercentage: this.clientDepositPercentage,
                        pagesPerAudioHour: this.pagesPerAudioHour,
                        foliosPerAudioHour: this.foliosPerAudioHour,
                        fixedBankCharge: this.fixedBankCharge,
                        variableBankChargePercentage: this.variableBankChargePercentage,
                        minimumCharge: this.minimumCharge,
                        costCenter: this.costCenter,
                        rateGroups: this.rateGroups
                    };

                    var url = this.apiBaseUrl;
                    if (!url.endsWith('/')) url += '/';

                    // this API call deals with storing either of those: courtRegistryId or quoteOrderTypeId related rates
                    url += "quote/rates";
                    axios
                        .post(url, JSON.stringify(data),
                            {
                                headers: {
                                    'Content-Type': 'application/json'
                                }
                            })
                        .then(response => {
                            // response.data contains rateGroupId
                            this.loadRateGroups(response.data);
                            this.getHistoricalCostCenters();
                            this.getHistoricalMaterialCodes();
                        })
                        .catch(ex => {
                            var currentErrors = this.handleAxiosException(ex);
                            this.showErrorsToast(currentErrors);
                        });
                }
            },

            resetRates: function () {
                if (this.selectedQuoteRateGroupId) {
                    this.$bvModal.msgBoxConfirm([this.$createElement('div', { domProps: { innerHTML: 'Are you sure you want to reset the current rates?<br/><br/>This action cannot be undone!' } })], {
                        title: 'Confirm Reset Rates',
                        size: 'md',
                        okVariant: 'danger',
                        okTitle: 'YES',
                        cancelTitle: 'NO',
                        centered: true
                    })
                        .then(value => {
                            if (value) {
                                this.rateGroupSelectionChanged();
                            }
                        });
                }
            },

            publishRates: function () {
                if (this.selectedQuoteRateGroupId) {
                    this.$bvModal.msgBoxConfirm([this.$createElement('div', { domProps: { innerHTML: 'Are you sure you want to publish the current rates?<br/><br/>This action cannot be undone!' } })], {
                        title: 'Confirm Publish Rates',
                        size: 'md',
                        okVariant: 'danger',
                        okTitle: 'YES',
                        cancelTitle: 'NO',
                        centered: true
                    })
                        .then(value => {
                            if (value) {
                                var url = this.apiBaseUrl;
                                if (!url.endsWith('/')) url += '/';
                                url += 'quote/publishrates/' + this.selectedQuoteRateGroupId;
                                axios
                                    .get(url)
                                    .then(() => {
                                        this.loadRateGroups(this.selectedQuoteRateGroupId);
                                    })
                                    .catch(ex => {
                                        var currentErrors = this.handleAxiosException(ex);
                                        this.showErrorsToast(currentErrors);
                                    });
                            }
                        });
                }
            }
        }, // methods

        mounted: function () {

            this.getHistoricalCostCenters();
            this.getHistoricalMaterialCodes();

            this.allCourtRegistryOptions.map(function (element) {
                element.group ="CourtRegistry";
                return element
            });

            this.allInterviewOrganisationOptions.map(function (element) {
                element.group = "InterviewOrganisation";
                return element
            });
        }

    } // export default
</script>
